/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { Check, Download, IndianRupee, Lock } from 'lucide-react';
import { useCallback, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, type BadgeTone } from '../../components/ui/badge.js';
import { Button } from '../../components/ui/button.js';
import { type Invoice, type InvoiceStatus } from '../../lib/api/finance.js';
import { formatIst } from '../../lib/utils/datetime.js';
import { formatInr } from '../../lib/utils/money.js';

const STATUS_TONE: Readonly<Record<InvoiceStatus, BadgeTone>> = {
  draft: 'slate',
  sent: 'violet',
  partial: 'amber',
  paid: 'green',
  overdue: 'rose',
  void: 'slate',
};

/** Timestamp cell (issued). */
function tsCell(iso: string | null): string {
  return iso !== null ? formatIst(iso, 'dd MMM yyyy') : '—';
}

/** Date-only cell (due date stored as YYYY-MM-DD). */
function dueCell(due: string | null): string {
  return due !== null ? formatIst(`${due}T00:00:00.000Z`, 'dd MMM yyyy') : '—';
}

/** Payment cell: "method · reference" (Figma), or "—" until paid. */
function paymentCell(invoice: Invoice, t: (k: string) => string): string {
  if (invoice.lastPaymentMethod === null) {
    return '—';
  }
  const method = t(`paymentMethod.${invoice.lastPaymentMethod}`);
  return invoice.lastPaymentRef !== null ? `${method} · ${invoice.lastPaymentRef}` : method;
}

interface InvoiceRowProps {
  readonly invoice: Invoice;
  readonly clientName: string;
  /** Admin/Finance "Record Payment". */
  readonly canPay: boolean;
  readonly onPay: (invoice: Invoice) => void;
  /** Admin/Finance "Mark Paid" — settles the full balance in one click. */
  readonly onMarkPaid: (invoice: Invoice) => void;
  readonly marking: boolean;
  /** Parent "Pay now" self-service (P0-3). */
  readonly canSelfPay: boolean;
  readonly onSelfPay: (invoice: Invoice) => void;
  /** Admin/Finance "Credit note" against a paid (locked) invoice (CR-010). */
  readonly onCreditNote: (invoice: Invoice) => void;
  /** Download the payment-receipt PDF (any invoice with a recorded payment, CR-010). */
  readonly onReceipt: (invoice: Invoice) => void;
}

/** One invoice row (Figma Finance → Invoices table). */
export function InvoiceTableRow({
  invoice,
  clientName,
  canPay,
  onPay,
  onMarkPaid,
  marking,
  canSelfPay,
  onSelfPay,
  onCreditNote,
  onReceipt,
}: InvoiceRowProps): JSX.Element {
  const { t } = useTranslation();
  const settled = invoice.status === 'paid' || invoice.status === 'void';
  const hasPayment = invoice.amountPaidPaise > 0;
  const paymentLabel = paymentCell(invoice, t);

  const handlePay = useCallback(() => {
    onPay(invoice);
  }, [onPay, invoice]);
  const handleMarkPaid = useCallback(() => {
    onMarkPaid(invoice);
  }, [onMarkPaid, invoice]);
  const handleSelfPay = useCallback(() => {
    onSelfPay(invoice);
  }, [onSelfPay, invoice]);
  const handleCreditNote = useCallback(() => {
    onCreditNote(invoice);
  }, [onCreditNote, invoice]);
  const handleReceipt = useCallback(() => {
    onReceipt(invoice);
  }, [onReceipt, invoice]);

  return (
    <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/60">
      <td className="px-5 py-3 font-medium text-slate-800 dark:text-white">
        {invoice.invoiceNumber}
      </td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{clientName}</td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">
        {formatInr(invoice.amountPaise)}
      </td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{tsCell(invoice.issuedAt)}</td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{dueCell(invoice.dueDate)}</td>
      <td className="px-5 py-3">
        <span className="inline-flex items-center gap-1.5">
          <Badge label={t(`invoiceStatus.${invoice.status}`)} tone={STATUS_TONE[invoice.status]} />
          {invoice.status === 'paid' && (
            <Lock
              className="h-3.5 w-3.5 text-slate-400"
              aria-label={t('finance.invoices.locked')}
            />
          )}
        </span>
      </td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{paymentLabel}</td>
      <td className="px-5 py-3 text-right">
        {canPay && !settled && (
          <div className="flex justify-end gap-1">
            <Button variant="ghost" size="sm" onClick={handlePay}>
              <IndianRupee className="h-4 w-4" aria-hidden="true" />
              {t('finance.invoices.recordPayment')}
            </Button>
            <Button variant="primary" size="sm" onClick={handleMarkPaid} disabled={marking}>
              <Check className="h-4 w-4" aria-hidden="true" />
              {t('finance.invoices.markPaid')}
            </Button>
          </div>
        )}
        {hasPayment && (
          <Button variant="ghost" size="sm" onClick={handleReceipt}>
            <Download className="h-4 w-4" aria-hidden="true" />
            {t('finance.invoices.receipt')}
          </Button>
        )}
        {canPay && invoice.status === 'paid' && (
          <Button variant="ghost" size="sm" onClick={handleCreditNote}>
            {t('finance.invoices.creditNote')}
          </Button>
        )}
        {canSelfPay && !settled && (
          <Button variant="primary" size="sm" onClick={handleSelfPay}>
            <IndianRupee className="h-4 w-4" aria-hidden="true" />
            {t('parent.billing.payNow')}
          </Button>
        )}
      </td>
    </tr>
  );
}
